import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store the getToken function from Clerk
let clerkGetToken = null;

// Set up the token getter function (called once from App.js)
export const setTokenGetter = (getTokenFn) => {
  clerkGetToken = getTokenFn;
};

// Legacy function for backwards compatibility
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Request interceptor - gets fresh token for every request
api.interceptors.request.use(
  async (config) => {
    if (clerkGetToken) {
      try {
        const token = await clerkGetToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.error('Failed to get auth token:', err);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth failures and surface errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Skip the global redirect when:
      //   - the user is on the dedicated checkout-resume page (it shows its
      //     own retry UI), or
      //   - the failing call is the checkout endpoint itself (caller handles
      //     errors and toasts inline, and the user is mid-upgrade flow).
      const url = error.config?.url || '';
      const onResumePage =
        typeof window !== 'undefined' &&
        window.location.pathname === '/checkout-resume';
      const isCheckoutCall = url.includes('/create-checkout-session');
      if (!onResumePage && !isCheckoutCall) {
        window.location.href = '/app#sign-in';
      }
      return Promise.reject(error);
    }

    const message =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';

    // Lazy-import toast to avoid circular dependency
    import('sonner').then(({ toast }) => {
      toast.error(message);
    });

    return Promise.reject(error);
  }
);

// Transactions
export const getTransactions = () => api.get('/transactions');
export const createTransaction = (data) => api.post('/transactions', data);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);
export const skipOccurrence = (id, date) => api.post(`/transactions/${id}/skip?date=${date}`);
export const unskipOccurrence = (id, date) => api.post(`/transactions/${id}/unskip?date=${date}`);
export const pauseTransaction = (id) => api.post(`/transactions/${id}/pause`);
export const resumeTransaction = (id) => api.post(`/transactions/${id}/resume`);
export const getOccurrences = (id, days = 365) => api.get(`/transactions/${id}/occurrences?days=${days}`);
export const updateOccurrenceAmounts = (id, customAmounts) => api.put(`/transactions/${id}/occurrences`, customAmounts);

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Ledger
export const getLedger = (days = 30, asOfDate = null) => {
  let url = `/ledger?days=${days}`;
  if (asOfDate) {
    url += `&as_of_date=${asOfDate}`;
  }
  return api.get(url);
};

// Migration - migrate guest data to user account
export const migrateGuestData = (guestData) => api.post('/migrate-guest-data', guestData);

// Workspace
export const getWorkspace = () => api.get('/workspace');
export const inviteToWorkspace = (email) => api.post('/workspace/invite', { email });
export const cancelInvite = (email) => api.delete(`/workspace/invite/${encodeURIComponent(email)}`);
export const getInviteInfo = (token) => api.get(`/workspace/invite/${token}`);
export const joinWorkspace = (token) => api.post('/workspace/join', { token });
export const updateWorkspaceName = (name) => api.put(`/workspace?name=${encodeURIComponent(name)}`);
export const updateMemberName = (name) => api.put('/workspace/member/name', { name });
export const leaveWorkspace = () => api.post('/workspace/leave');
export const removeMember = (userId) => api.delete(`/workspace/member/${userId}`);

// CSV Import
export const getAccounts = () => api.get('/accounts');
export const createAccount = (data) => api.post('/accounts', data);
export const deleteAccount = (accountId) => api.delete(`/accounts/${accountId}`);
export const getImportStatus = () => api.get('/csv/import-status');

export const previewCSV = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/csv/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const analyzeCSV = (file, accountId, analysisMonths, columnMapping) => {
  const formData = new FormData();
  formData.append('file', file);
  
  let url = `/csv/analyze?account_id=${accountId}&analysis_months=${analysisMonths}`;
  url += `&date_column=${columnMapping.date_column}`;
  url += `&description_column=${columnMapping.description_column}`;
  
  if (columnMapping.amount_column !== null && columnMapping.amount_column !== undefined) {
    url += `&amount_column=${columnMapping.amount_column}`;
  }
  if (columnMapping.debit_column !== null && columnMapping.debit_column !== undefined) {
    url += `&debit_column=${columnMapping.debit_column}`;
  }
  if (columnMapping.credit_column !== null && columnMapping.credit_column !== undefined) {
    url += `&credit_column=${columnMapping.credit_column}`;
  }
  
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const generateForecast = (accountId, approvedGroups, forecastMonths) => {
  return api.post('/csv/generate-forecast', {
    account_id: accountId,
    approved_groups: approvedGroups,
    forecast_months: forecastMonths,
    replace_existing: true
  });
};

// ---- Stripe ----
export const createCheckoutSession = (priceId, originUrl) =>
  api.post('/create-checkout-session', { priceId, originUrl });

export const createBillingPortal = (returnUrl) =>
  api.post('/billing-portal', { returnUrl });

export const getMySubscription = () => api.get('/subscription/me');

export default api;
